import React, { useContext, useEffect, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  CMRItemResponse,
  GetCmrCustomDataV2ResponseOutDTO,
  GetCMRDetailDocResponse,
  NullableBigDecimalWrapper,
  NullableStringWrapper,
  UpdateCmrCustomDataV2ManipulationUnitRequestInDTO,
} from '@api/ringil-api.v2.tsx';
import useForm from '@hooks/useForm/useForm.tsx';
import * as yup from 'yup';
import CMRTextFieldBlock from '@components/CMR/CMRTextFieldBlock.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import CMRNumberFieldBlock from '@components/CMR/CMRNumberFieldBlock.tsx';
import CMRManipulationUnit from '@components/CMR/CMRManipulationUnit.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';
import SvgPlus from '@icons/plus.svg?react';
import clsx from 'clsx';
import COHeadline from '@components/framework/headlines/COHeadline.tsx';

interface ComponentProps {
  expeditionId: string;
  applicationId: string;
  onSuccessEdit?: () => void;
  originalData?: GetCMRDetailDocResponse;
}

export interface ManipulationUnitFormData {
  mpSignoNumber?: NullableStringWrapper;
  mpCount?: NullableBigDecimalWrapper;
  mpPackagingName?: NullableStringWrapper;
  mpName?: NullableStringWrapper;
  mpStatisticsNumber?: NullableStringWrapper;
  mpCompleteWeight?: NullableBigDecimalWrapper;
  mpVolume?: NullableBigDecimalWrapper;
  position?: number;
}

interface UpdateCmrCustomFormData {
  completeSender?: NullableStringWrapper;
  completeConsignee?: NullableStringWrapper;
  successiveCarriers?: NullableStringWrapper;
  attachedDocumentsNumber5?: NullableStringWrapper;
  sendersInstructions?: NullableStringWrapper;
  toBePaidBy?: NullableStringWrapper;
  instructionAsToPaymentForCarriage?: NullableStringWrapper;
  specialAgreements?: NullableStringWrapper;
  estabilishedIn?: NullableStringWrapper;
  dzvvNumber?: NullableStringWrapper;
  rideNumber?: NullableStringWrapper;
  borderCrossings?: NullableStringWrapper;
  others?: NullableStringWrapper;
  attachedDocumentsNumber30?: NullableStringWrapper;
  confirmationOfCustomsPapers?: NullableStringWrapper;
  cashOnDelivery?: NullableStringWrapper;
  carriersReservationsAndObservations?: NullableStringWrapper;
  weight?: NullableBigDecimalWrapper;
  unClass?: NullableStringWrapper;
  manipulationUnits?: ManipulationUnitFormData[];
}

const CMREditForm: React.FC<ComponentProps> = ({ expeditionId, applicationId, onSuccessEdit, originalData }) => {
  const {
    getCustomDataForCMRV2,
    updateCustomDataForCMRV2,
  } = useContext(ApiContext);
  const { tr } = useTranslationLgs();

  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<GetCmrCustomDataV2ResponseOutDTO | null>(null);

  useEffect(() => {
    setLoading(true);
    getCustomDataForCMRV2(
      expeditionId,
      applicationId,
      data => {
        setData(data);
        setLoading(false);
      },
      null,
      {
        onError: (_error, showStandardError) => {
          setLoading(false);
          showStandardError();
        }
      }
    );
  }, [expeditionId, applicationId, getCustomDataForCMRV2]);

  const createNullableString = (value?: string, originalValue?: string): NullableStringWrapper | null => {
    const isNewEmpty = value === undefined || value === null || value === '';
    const isOriginalEmpty = originalValue === undefined || originalValue === null;
    if (isOriginalEmpty && isNewEmpty) return null;
    if (isNewEmpty) return { value: null };
    return { value };
  };

  const createNullableNumber = (value?: number, originalValue?: number): NullableBigDecimalWrapper | null => {
    const isNewEmpty = value === undefined || value === null;
    const isOriginalEmpty = originalValue === undefined || originalValue === null;
    if (isOriginalEmpty && isNewEmpty) return null;
    if (isNewEmpty) return { value: null };
    return { value };
  };

  const isUnitEmpty = (unit: UpdateCmrCustomDataV2ManipulationUnitRequestInDTO) =>
    !unit.mpSignoNumber?.value &&
    !unit.mpCount?.value &&
    !unit.mpPackagingName?.value &&
    !unit.mpName?.value &&
    !unit.mpStatisticsNumber?.value &&
    !unit.mpCompleteWeight?.value &&
    !unit.mpVolume?.value;

  const form = useForm<UpdateCmrCustomFormData>(
    yup.object().shape({}),
    {},
    values => {
      setLoading(true);

      const filteredValues = {
        ...values,
        manipulationUnits: Array.isArray(values.manipulationUnits)
          ? values.manipulationUnits.length > 0
            ? values.manipulationUnits.filter(mu => !isUnitEmpty(mu))
            : null
          : null,
      };

      updateCustomDataForCMRV2(expeditionId, applicationId, filteredValues, () => {
        onSuccessEdit?.();
        setLoading(false);
      });
    },
    false,
    false,
  );

  useEffect(() => {
    if (!loading && data === null) {
      // API returned, initialize with empty data if null
      setData({} as GetCmrCustomDataV2ResponseOutDTO);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, data]);

  useEffect(() => {
    if (!data) return;

    form.setValues({
      completeSender: data?.completeSender ? { value: data.completeSender } : null,
      completeConsignee: data?.completeConsignee ? { value: data.completeConsignee } : null,
      successiveCarriers: data?.successiveCarriers ? { value: data.successiveCarriers } : null,
      carriersReservationsAndObservations: data?.carriersReservationsAndObservations ? { value: data.carriersReservationsAndObservations } : null,
      attachedDocumentsNumber5: data?.attachedDocumentsNumber5 ? { value: data.attachedDocumentsNumber5 } : null,
      sendersInstructions: data?.sendersInstructions ? { value: data.sendersInstructions } : null,
      toBePaidBy: data?.toBePaidBy ? { value: data.toBePaidBy } : null,
      cashOnDelivery: data?.cashOnDelivery ? { value: data.cashOnDelivery } : null,
      instructionAsToPaymentForCarriage: data?.instructionAsToPaymentForCarriage ? { value: data.instructionAsToPaymentForCarriage } : null,
      estabilishedIn: data?.estabilishedIn ? { value: data.estabilishedIn } : null,
      specialAgreements: data?.specialAgreements ? { value: data.specialAgreements } : null,
      dzvvNumber: data?.dzvvNumber ? { value: data.dzvvNumber } : null,
      borderCrossings: data?.borderCrossings ? { value: data.borderCrossings } : null,
      others: data?.others ? { value: data.others } : null,
      weight: data?.weight ? { value: data.weight } : null,
      rideNumber: data?.rideNumber ? { value: data.rideNumber } : null,
      attachedDocumentsNumber30: data?.attachedDocumentsNumber30 ? { value: data.attachedDocumentsNumber30 } : null,
      confirmationOfCustomsPapers: data?.confirmationOfCustomsPapers ? { value: data.confirmationOfCustomsPapers } : null,
      unClass: data?.unClass ? { value: data.unClass } : null,
      manipulationUnits: data?.manipulationUnits?.length ? data.manipulationUnits.map(mu => {
        return {
          mpSignoNumber: mu.mpSignoNumber ? { value: mu.mpSignoNumber } : null,
          mpCount: mu.mpCount ? { value: mu.mpCount } : null,
          mpPackagingName: mu.mpPackagingName ? { value: mu.mpPackagingName } : null,
          mpName: mu.mpName ? { value: mu.mpName } : null,
          mpStatisticsNumber: mu.mpStatisticsNumber ? { value: mu.mpStatisticsNumber } : null,
          mpCompleteWeight: mu.mpCompleteWeight ? { value: mu.mpCompleteWeight } : null,
          mpVolume: mu.mpVolume ? { value: mu.mpVolume } : null,
          position: mu.position,
        };
      }) : [{
        mpSignoNumber: null,
        mpCount: null,
        mpPackagingName: null,
        mpName: null,
        mpStatisticsNumber: null,
        mpCompleteWeight: null,
        mpVolume: null,
        position: 0,
      }],
    });
  }, [data]);

  if (loading) {
    return <div className="p-4">{tr('CMREditForm.loading', 'Loading...')}</div>;
  }

  if (!originalData) {
    return <div className="p-4 text-red-500">{tr('CMREditForm.noData', 'No original data available')}</div>;
  }

  return (
    <div className="flex flex-col w-full">
      <div className="flex flex-col md:flex-row w-full">
        <div className="flex flex-col w-full md:w-1/2">
          <CMRTextFieldBlock
            indexLabel={1}
            label={tr('CMREditForm.completeSenderLabel', 'Sender')}
            sublabel={tr('CMREditForm.completeSenderSubLabel', 'Sender')}
            value={form.values.completeSender?.value}
            originalValue={originalData?.completeSender}
            onChange={(value) => {
              form.setFieldValue(form.names.completeSender, createNullableString(value, data?.completeSender));
            }}
          />
          <CMRTextFieldBlock
            indexLabel={2}
            label={tr('CMREditForm.completeConsigneeLabel', 'Consignee')}
            sublabel={tr('CMREditForm.completeConsigneeSubLabel', 'Consignee')}
            value={form.values.completeConsignee?.value}
            originalValue={originalData?.completeConsignee}
            onChange={(value) => {
              form.setFieldValue(form.names.completeConsignee, createNullableString(value, data?.completeConsignee));
            }}
          />
          <CMRTextFieldBlock
            indexLabel={3}
            label={tr('CMREditForm.unloadingDateAndPlaceLabel', 'Place and date of unloading')}
            sublabel={tr('CMREditForm.unloadingDateAndPlaceSubLabel', 'Place and date of unloading')}
            originalValue={originalData?.unloadingDateAndPlace}
            note={tr('CMREditForm.editTransportDetailsInstruction', 'To edit transport details, go to the Transport tab.')}
            editable={false}
          />
          <CMRTextFieldBlock
            indexLabel={4}
            label={tr('CMREditForm.loadingDateAndPlaceLabel', 'Place and date of loading')}
            sublabel={tr('CMREditForm.loadingDateAndPlaceSubLabel', 'Place and date of loading')}
            originalValue={originalData?.loadDateAndPlace}
            note={tr('CMREditForm.editTransportDetailsInstruction', 'To edit transport details, go to the Transport tab.')}
            editable={false}
          />
        </div>
        <div className="flex flex-col w-full md:w-1/2">
          <div
            className="hidden md:block w-full h-[135px] border border-solid border-content-secondary">
          </div>
          <CMRTextFieldBlock
            indexLabel={16}
            label={tr('CMREditForm.carrierLabel', 'Carrier')}
            sublabel={tr('CMREditForm.carrierSubLabel', 'Carrier')}
            originalValue={originalData?.carrier ? [originalData.carrier.name, originalData.carrier.countryCode3, originalData.carrier.city, originalData.carrier.streetNr, originalData.carrier.zipCode].filter(Boolean).join(', ') : ''}
            note={tr('CMREditForm.editTransportDetailsInstruction', 'To edit transport details, go to the Transport tab.')}
            editable={false}
          />
          <CMRTextFieldBlock
            indexLabel={17}
            label={tr('CMREditForm.successiveCarriersLabel', 'Successive carriers')}
            sublabel={tr('CMREditForm.successiveCarriersSubLabel', 'Successive carriers')}
            value={form.values.successiveCarriers?.value}
            originalValue={originalData?.successiveCarriers}
            onChange={(value) => {
              form.setFieldValue(form.names.successiveCarriers, createNullableString(value, data?.successiveCarriers));
            }}
          />
          <CMRTextFieldBlock
            indexLabel={18}
            label={tr('CMREditForm.carriersReservationsAndObservationsLabel', 'Carrier\'s reservations and observations')}
            sublabel={tr('CMREditForm.carriersReservationsAndObservationsSubLabel', 'Carrier\'s reservations and observations')}
            value={form.values.carriersReservationsAndObservations?.value}
            originalValue={originalData?.carrierReservationsAndObservations}
            onChange={(value) => {
              form.setFieldValue(form.names.carriersReservationsAndObservations, createNullableString(value, data?.carriersReservationsAndObservations));
            }}
          />
        </div>
      </div>
      <CMRTextFieldBlock
        indexLabel={5}
        label={tr('CMREditForm.attachedDocumentsNumber5Label', 'Attached documents type')}
        sublabel={tr('CMREditForm.attachedDocumentsNumber5SubLabel', 'Attached documents type')}
        value={form.values.attachedDocumentsNumber5?.value}
        originalValue={originalData?.attachedDocumentTypes}
        onChange={(value) => {
          form.setFieldValue(form.names.attachedDocumentsNumber5, createNullableString(value, data?.attachedDocumentsNumber5));
        }}
      />
      <div className="flex flex-col px-2.5 pt-2.5 border border-solid border-content-secondary bg-base-base">
        <COHeadline
          variant={'h2'}
          title={tr('CMREditForm.manipulationUnitsSectionTitle', 'Manipulation units')}
        />
        {form.values.manipulationUnits?.slice()
          .sort((a, b) => (a.position ?? 0) - (b.position ?? 0)).map((mu, index) => (
          <div key={index}>
            <div key={index} className={clsx('mx-2.5',
              index != 0 ? 'border-[0.5px] border-solid border-content-secondary' : '',
            )} />
            <div className="flex flex-row w-full pt-2 justify-between">
              <span className="text-black text-xs font-dm font-normal leading-normal tracking-[0.24px]">
                {tr('CMREditForm.manipulationUnitLabel', '{{index}} Item', { index: index + 1 })}
              </span>
              <RingilButton
                icon={<SvgDelete />}
                onClick={() => {
                  const units = Array.isArray(form.values.manipulationUnits) ? [...form.values.manipulationUnits] : [];
                  units.splice(index, 1);
                  form.setFieldValue(form.names.manipulationUnits, units);
                }}
                mode="table"
              />
            </div>
            <CMRManipulationUnit
              value={mu}
              originalValue={originalData?.items?.find(omu => omu.position === mu.position)}
              onChange={(value) => {
                const units = Array.isArray(form.values.manipulationUnits) ? [...form.values.manipulationUnits] : [];
                units[index] = value;
                form.setFieldValue(form.names.manipulationUnits, units);
              }}
            />
          </div>
        ))}
        {(originalData?.items && originalData.items.length > (form.values.manipulationUnits?.length || 0)) && (
          <div
            className="text-[12px] font-dm-sans font-bold leading-normal tracking-[0.24px] text-content-tertiary"
          >
            <span>
              {tr('CMREditForm.additionalManipulation Units', 'Additional manipulation units: {{count}}', { count: originalData.items.length - (form.values.manipulationUnits?.length || 0) })}
            </span>
            <table className="w-full table-fixed border-collapse">
            <thead>
            <tr>
              <th className="w-1/7 text-xs font-bold text-center">{tr('CMREditForm.mpSignoNumberLabel', 'Arks and nos')}</th>
              <th className="w-1/7 text-xs font-bold text-center">{tr('CMREditForm.mpCountLabel', 'Number of packages')}</th>
              <th className="w-1/7 text-xs font-bold text-center">{tr('CMREditForm.mpPackagingNameLabel', 'Method of packaging')}</th>
              <th className="w-1/7 text-xs font-bold text-center">{tr('CMREditForm.mpNameLabel', 'Nature of the goods')}</th>
              <th className="w-1/7 text-xs font-bold text-center">{tr('CMREditForm.mpStatisticsNumberLabel', 'Statistical number')}</th>
              <th className="w-1/7 text-xs font-bold text-center">{tr('CMREditForm.mpCompleteWeightLabel', 'Gross weight in kg')}</th>
              <th className="w-1/7 text-xs font-bold text-center">{tr('CMREditForm.mpVolumeLabel', 'Volume in m³')}</th>
            </tr>
            </thead>
            <tbody>
            {originalData.items
              ?.sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
              .slice(form.values.manipulationUnits?.length)
              .map((row: CMRItemResponse, idx: number) => (
                <tr key={idx}>
                  <td className="text-center">{row.signoNumber}</td>
                  <td className="text-center">{row.count}</td>
                  <td className="text-center">{row.packagingName}</td>
                  <td className="text-center">{row.name}</td>
                  <td className="text-center">{row.statisticsNumber}</td>
                  <td className="text-center">{row.completeWeight}</td>
                  <td className="text-center">{row.volume}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        <RingilButton
          label={tr('CMREditForm.addManipulationUnitButton', 'Add manipulation unit')}
          icon={<SvgPlus />}
          onClick={() => {
            const newUnit = {
              mpSignoNumber: null,
              mpCount: null,
              mpPackagingName: null,
              mpName: null,
              mpStatisticsNumber: null,
              mpCompleteWeight: null,
              mpVolume: null,
              position: form.values.manipulationUnits?.length ?? 0,
            } as UpdateCmrCustomDataV2ManipulationUnitRequestInDTO;

            const units = Array.isArray(form.values.manipulationUnits)
              ? [...form.values.manipulationUnits, newUnit]
              : [newUnit];

            form.setFieldValue(form.names.manipulationUnits, units);
          }}
          mode="text-only"
        />
        <CMRTextFieldBlock
          label={tr('CMREditForm.unClassLabel', 'UN Class, Letter, ADR')}
          sublabel={tr('CMREditForm.unClassSubLabel', 'UN Class, Letter, ADR')}
          value={form.values.unClass?.value}
          originalValue={originalData?.unClass}
          onChange={(value) => {
            form.setFieldValue(form.names.unClass, createNullableString(value, data?.unClass));
          }}
          borders={false}
        />
      </div>
      <div className="flex flex-col md:flex-row w-full">
        <CMRTextFieldBlock
          indexLabel={13}
          label={tr('CMREditForm.sendersInstructionsLabel', 'Sender\'s instructions')}
          sublabel={tr('CMREditForm.sendersInstructionsSubLabel', 'Sender\'s instructions')}
          value={form.values.sendersInstructions?.value}
          originalValue={originalData?.sendersInstructions}
          onChange={(value) => {
            form.setFieldValue(form.names.sendersInstructions, createNullableString(value, data?.sendersInstructions));
          }}
        />
        <CMRTextFieldBlock
          indexLabel={19}
          label={tr('CMREditForm.toBePaidByLabel', 'To be paid by')}
          sublabel={tr('CMREditForm.toBePaidBySubLabel', 'To be paid by')}
          value={form.values.toBePaidBy?.value}
          originalValue={originalData?.toBePaidBy}
          onChange={(value) => {
            form.setFieldValue(form.names.toBePaidBy, createNullableString(value, data?.toBePaidBy));
          }}
        />
      </div>
      <CMRTextFieldBlock
        indexLabel={14}
        label={tr('CMREditForm.cashOnDeliveryLabel', 'Cash on delivery')}
        sublabel={tr('CMREditForm.cashOnDeliverySubLabel', 'Cash on delivery')}
        value={form.values.cashOnDelivery?.value}
        originalValue={originalData?.cashOnDelivery}
        onChange={(value) => {
          form.setFieldValue(form.names.cashOnDelivery, createNullableString(value, data?.cashOnDelivery));
        }}
      />
      <div className="flex flex-col md:flex-row w-full">
        <div className="flex flex-col w-full md:w-1/2">
          <CMRTextFieldBlock
            indexLabel={15}
            label={tr('CMREditForm.instructionAsToPaymentForCarriageLabel', 'Instruction as to payment for carriage')}
            sublabel={tr('CMREditForm.instructionAsToPaymentForCarriageSubLabel', 'Instruction as to payment for carriage')}
            value={form.values.instructionAsToPaymentForCarriage?.value}
            originalValue={originalData?.instrPaymentForCarriage}
            onChange={(value) => {
              form.setFieldValue(form.names.instructionAsToPaymentForCarriage, createNullableString(value, data?.instructionAsToPaymentForCarriage));
            }}
          />
          <CMRTextFieldBlock
            indexLabel={21}
            label={tr('CMREditForm.estabilishedInLabel', 'Established in')}
            sublabel={tr('CMREditForm.estabilishedInSubLabel', 'Established in')}
            value={form.values.estabilishedIn?.value}
            originalValue={originalData?.establishedIn}
            onChange={(value) => {
              form.setFieldValue(form.names.estabilishedIn, createNullableString(value, data?.estabilishedIn));
            }}
          />
        </div>
        <div className="flex flex-col w-full md:w-1/2">
          <CMRTextFieldBlock
            indexLabel={20}
            label={tr('CMREditForm.specialAgreementsLabel', 'Special agreements')}
            sublabel={tr('CMREditForm.specialAgreementsSubLabel', 'Special agreements')}
            value={form.values.specialAgreements?.value}
            originalValue={originalData?.specialAgreements}
            onChange={(value) => {
              form.setFieldValue(form.names.specialAgreements, createNullableString(value, data?.specialAgreements));
            }}
          />
          <CMRTextFieldBlock
            label={tr('CMREditForm.dateLabel', 'Date')}
            sublabel={tr('CMREditForm.dateSubLabel', 'Date')}
            originalValue={originalData?.loadDateAndPlace}
            editable={false}
          />
        </div>
      </div>
      <div className="flex flex-col md:flex-row w-full">
        <CMRTextFieldBlock
          indexLabel={22}
          label={tr('CMREditForm.signatureAndStampSenderLabel', 'Signature and stamp of sender')}
          sublabel={tr('CMREditForm.signatureAndStampSenderSubLabel', 'Signature and stamp of sender')}
          editable={false}
        />
        <CMRTextFieldBlock
          indexLabel={23}
          label={tr('CMREditForm.signatureAndStampCarrierLabel', 'Signature and stamp of carrier')}
          sublabel={tr('CMREditForm.signatureAndStampCarrierSubLabel', 'Signature and stamp of carrier')}
          editable={false}
        />
        <CMRTextFieldBlock
          indexLabel={24}
          label={tr('CMREditForm.goodsReceivedDateLabel', 'Goods received date')}
          sublabel={tr('CMREditForm.goodsReceivedDateSubLabel', 'Goods received date')}
          editable={false}
        />
      </div>
      <div className="flex flex-col md:flex-row w-full">
        <div className="flex flex-col w-full md:w-1/2">
          <CMRTextFieldBlock
            indexLabel={25}
            label={tr('CMREditForm.carPlateLabel', 'Car plate')}
            sublabel={tr('CMREditForm.carPlateSubLabel', 'Car plate')}
            originalValue={originalData?.carPlates}
            note={tr('CMREditForm.editTransportDetailsInstruction', 'To edit transport details, go to the Transport tab.')}
            editable={false}
          />
          <CMRTextFieldBlock
            indexLabel={27}
            label={tr('CMREditForm.dzvvNumberLabel', 'DZVV number')}
            sublabel={tr('CMREditForm.dzvvNumberSubLabel', 'DZVV number')}
            value={form.values.dzvvNumber?.value}
            originalValue={originalData?.dzvvNumber}
            onChange={(value) => {
              form.setFieldValue(form.names.dzvvNumber, createNullableString(value, data?.dzvvNumber));
            }}
          />
          <CMRTextFieldBlock
            indexLabel={29}
            label={tr('CMREditForm.borderCrossingsLabel', 'Border crossings')}
            sublabel={tr('CMREditForm.borderCrossingsSubLabel', 'Border crossings')}
            value={form.values.borderCrossings?.value}
            originalValue={originalData?.borderCrossings}
            onChange={(value) => {
              form.setFieldValue(form.names.borderCrossings, createNullableString(value, data?.borderCrossings));
            }}
          />
          <CMRTextFieldBlock
            indexLabel={31}
            label={tr('CMREditForm.othersLabel', 'Others')}
            sublabel={tr('CMREditForm.othersSubLabel', 'Others')}
            value={form.values.others?.value}
            originalValue={originalData?.others}
            onChange={(value) => {
              form.setFieldValue(form.names.others, createNullableString(value, data?.others));
            }}
          />
        </div>
        <div className="flex flex-col w-full md:w-1/2">
          <CMRNumberFieldBlock
            indexLabel={26}
            label={tr('CMREditForm.weightLabel', 'Payload weight')}
            sublabel={tr('CMREditForm.weightSubLabel', 'Payload weight')}
            value={form.values.weight?.value}
            originalValue={originalData?.weight}
            onChange={(value) => {
              form.setFieldValue(form.names.weight, createNullableNumber(value, data?.weight));
            }}
          />
          <CMRTextFieldBlock
            indexLabel={28}
            label={tr('CMREditForm.rideNumberLabel', 'Ride number')}
            sublabel={tr('CMREditForm.rideNumberSubLabel', 'Ride number')}
            value={form.values.rideNumber?.value}
            originalValue={originalData?.rideNumber}
            onChange={(value) => {
              form.setFieldValue(form.names.rideNumber, createNullableString(value, data?.rideNumber));
            }}
          />
          <CMRTextFieldBlock
            indexLabel={30}
            label={tr('CMREditForm.attachedDocumentsNumber30Label', 'Attached documents')}
            sublabel={tr('CMREditForm.attachedDocumentsNumber30SubLabel', 'Attached documents')}
            value={form.values.attachedDocumentsNumber30?.value}
            originalValue={originalData?.accompanyingDocuments}
            onChange={(value) => {
              form.setFieldValue(form.names.attachedDocumentsNumber30, createNullableString(value, data?.attachedDocumentsNumber30));
            }}
          />
          <CMRTextFieldBlock
            label={tr('CMREditForm.confirmationOfCustomsPapersLabel', 'Confirmation of customs papers')}
            sublabel={tr('CMREditForm.confirmationOfCustomsPapersSubLabel', 'Confirmation of customs papers')}
            value={form.values.confirmationOfCustomsPapers?.value}
            originalValue={originalData?.confirmationOfCustomsPapers}
            onChange={(value) => {
              form.setFieldValue(form.names.confirmationOfCustomsPapers, createNullableString(value, data?.confirmationOfCustomsPapers));
            }}
          />
        </div>
      </div>
      <div className="flex flex-row mt-2">
        <RingilButton
          label={tr(`CMREditForm.regenerate`, `Regenerate`)}
          onClick={() => form.validateAndSend()}
          mode={'filled'}
          size={'big'}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default CMREditForm;
